/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { Content } from '@prismicio/client';
import { FAQPageJsonLd } from 'next-seo';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { FaqProps, normalizeFAQs, normalizePrismicSEO } from 'src/prismic/utils';
import { Faqs } from 'src/components/core/faqs';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { MorePagesSection } from 'src/components/sections/more-pages-section';
import { PrismicRichText } from 'src/components/prismic';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Section } from 'src/components/core/layout/section';
import { Text } from 'src/components/core/text';
import { createPrimsicClient } from 'src/prismic/client';
import { routeResolve } from 'src/core/utils/routes';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslation } from 'next-i18next';
import { withSettings } from 'src/hocs/with-settings';
import chevronLeftSvg from 'src/assets/svgs/chevron-left.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: Content.FaqsCategoriesDocumentData;
  faqs: FaqProps[];
  faqsPagesData: { slug: string; title: string }[];
};

/**
 * `StyledSection` styled component.
 */

const StyledSection = styled(Section)`
  --section-padding-top: var(--space-sm);
`;

/**
 * `FAQsCategory` page.
 */

const FAQsCategory: NextPage<Props> = ({ data, faqs, faqsPagesData }) => {
  const { t } = useTranslation();
  const isMobile = useBreakpoint(0, 'md');

  return (
    <>
      <SEO {...normalizePrismicSEO(data)} />

      <FAQPageJsonLd
        mainEntity={faqs?.map(({ answer, question }) => ({
          acceptedAnswerText: answer,
          questionName: question
        }))}
      />

      <AnimationController staggerMobile>
        <StyledSection hasNavbarOffset>
          <Grid>
            <SubGrid style={{ paddingInline: isMobile ? 'var(--space-sm)' : 0 }}>
              <FadeInUp staggerMobile>
                <Button
                  aria-label={t('common:actions.back')}
                  color={'var(--color-slate50)'}
                  colorTheme={'text'}
                  href={routeResolve('faqs')}
                  icon={chevronLeftSvg}
                  size={'medium'}
                >
                  <Text color={'var(--color-slate50)'} variant={'labelL'}>
                    {t('common:actions.back')}
                  </Text>
                </Button>
              </FadeInUp>

              <Heading
                asTitle={'h1'}
                description={<PrismicRichText field={data?.description} />}
                staggerMobile
                style={{ marginBottom: 'var(--space-sm)', paddingTop: '48px' }}
                title={data?.title as string}
              />
            </SubGrid>
          </Grid>

          <Grid data-has-offset-gutter>
            <SubGrid>
              <Faqs faqs={faqs} staggerMobile />
            </SubGrid>
          </Grid>
        </StyledSection>

        <FadeInUp staggerMobile>
          <MorePagesSection
            data={faqsPagesData?.map(({ slug, title }) => ({
              href: routeResolve('faqsCategoria', { slug }),
              title
            }))}
          />
        </FadeInUp>
      </AnimationController>
    </>
  );
};

/**
 * Export `getStaticPaths`.
 */

export const getStaticPaths: GetStaticPaths = async () => {
  const client = createPrimsicClient();
  const categories = await client.getAllByType('faqs_categories');

  return {
    fallback: 'blocking',
    paths: categories.map(({ uid }) => ({
      params: {
        slug: uid
      }
    }))
  };
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = withSettings<any>(async ctx => {
  const { locale, params, previewData } = ctx;
  const slug = params?.slug as string;
  const client = createPrimsicClient({ previewData });

  const faqs = await client.getAllByType('faq', {
    orderings: {
      direction: 'asc',
      field: 'document.first_publication_date'
    }
  });

  const faqsPagesData = await client.getAllByType('faqs_categories', {
    orderings: {
      direction: 'asc',
      field: 'document.first_publication_date'
    }
  });

  const { data } = await client.getByUID('faqs_categories', slug);

  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'faqs'])),
      data,
      faqs: normalizeFAQs(faqs, slug),
      faqsPagesData: faqsPagesData
        .filter(page => page.uid !== slug)
        .map(({ data, uid }) => ({ slug: uid, title: data.title }))
    },
    revalidate: 60
  };
});

/**
 * Export `FAQsCategory` page.
 */

export default FAQsCategory;
